import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { updateOnboardStatus } from '../../../services/coachAPI';

const Step6Complete = ({ onComplete, coachData }) => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const updateLocalUserData = (status) => {
    const storageKeys = ['coachUser', 'currentUser'];

    storageKeys.forEach((key) => {
      const storedUser = localStorage.getItem(key);

      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);

        parsedUser.onboardStatus = status;

        localStorage.setItem(key, JSON.stringify(parsedUser));
      }
    });
  };

  const handleFinish = async () => {
    setSaving(true);
    setError('');

    try {
      const coachId = coachData?.id || coachData?.uid || coachData?._id;

      if (!coachId) {
        throw new Error('Coach ID not found.');
      }

      await updateOnboardStatus(coachId, 'completed');

      updateLocalUserData('completed');

      if (onComplete) {
        await onComplete();
      }

      setDone(true);
    } catch (err) {
      setError(err.message || 'Failed to complete onboarding.');
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="text-center space-y-6 py-8">
        <div className="mx-auto h-24 w-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg animate-bounce">
          <Sparkles className="h-12 w-12 text-white" />
        </div>

        <div>
          <h2 className="text-3xl font-bold text-gray-900">Onboarding Complete!</h2>
          <p className="text-lg text-gray-600 mt-2">Welcome to the YAU coaching team!</p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-xl p-6 max-w-md mx-auto">
          <div className="space-y-3 text-left">
            {[
              'Coach Agreement signed',
              'Non-Compete Agreement signed',
              'Additional Agreements acknowledged',
              'Background Check authorized',
              'Training Courses completed'
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-green-800">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-gray-600">
          You now have full access to the Coach Portal. Start exploring your dashboard, managing your teams, and connecting with families.
        </p>

        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 shadow-lg transition-all text-lg"
        >
          Go to Coach Dashboard
          <ArrowRight className="h-5 w-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="text-center space-y-6 py-8">
      <div className="mx-auto h-24 w-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
        <CheckCircle className="h-12 w-12 text-white" />
      </div>

      <div>
        <h2 className="text-3xl font-bold text-gray-900">Final Step: Complete</h2>
        <p className="text-lg text-gray-600 mt-2">
          All onboarding steps have been completed. You're almost there!
        </p>
      </div>

      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 max-w-lg mx-auto">
        <h3 className="font-semibold text-green-900 mb-3">Onboarding Summary</h3>

        <div className="space-y-2 text-left">
          {[
            { label: 'Coach Agreement', status: 'Signed' },
            { label: 'Non-Compete Agreement', status: 'Signed' },
            { label: 'Additional Agreements', status: 'Acknowledged' },
            { label: 'Background Check Auth', status: 'Authorized' },
            { label: 'Training Courses', status: 'Completed' }
          ].map((item, i) => (
            <div key={i} className="flex items-center justify-between p-2 bg-white rounded-lg">
              <span className="text-sm text-gray-700">{item.label}</span>
              <span className="flex items-center gap-1 text-sm text-green-700 font-medium">
                <CheckCircle className="h-4 w-4" />
                {item.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700 max-w-md mx-auto">
          {error}
        </div>
      )}

      <p className="text-gray-500 text-sm">
        Click the button below to finalize your onboarding and gain access to the Coach Portal.
      </p>

      <button
        type="button"
        onClick={handleFinish}
        disabled={saving}
        className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 shadow-lg transition-all text-lg"
      >
        {saving ? (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
        ) : (
          <Sparkles className="h-5 w-5" />
        )}

        {saving ? 'Finalizing...' : 'Complete Onboarding & Access Coach Portal'}
      </button>
    </div>
  );
};

export default Step6Complete;